import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderValue } from '@/hooks/tab';
import { useItem } from '@/hooks/item';
import { useOrder } from '@/hooks/order';
import { useCurrentOrderItem } from '@/hooks/reference';
import { Skeleton } from '@mui/material';
import { ServiceErrorView } from '@/components/atoms/Error';
import { Heading } from '@/components/atoms/Sidebar';
import ResultView from '@/components/molecules/sidebar/Result';
import Item from '@/components/molecules/sidebar/Item';
import styles from './Common.module.scss';

const View: React.FC = () => {
  const { t } = useTranslation();
  const [inputValue] = useOrderValue();
  const [currentItem, setCurrentItem] = useCurrentOrderItem();
  const data = useOrder(Number(inputValue));

  useEffect(() => {
    if (currentItem) setCurrentItem('');
  }, [inputValue]);

  const items =
    data.kind === 'Loaded' && data.data.products
      ? data.data.products.map(({ productId }) => productId).filter((id) => id !== '')
      : [];

  return (
    <div
      className={styles.container}
      data-cy="SideBar-Order"
    >
      <Heading label={`${t('sidebar.result_for')} '${inputValue}'`} />
      {data.kind === 'Called' && (
        <Skeleton
          height={100}
          variant="rounded"
        />
      )}
      {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
      {data.kind === 'Loaded' && (
        <>
          <ResultView
            onClick={() => setCurrentItem('')}
            head={`${t('sidebar.order')} #${data.data.orderId}`}
            sub={`${items.length} ${items.length > 1 ? t('sidebar.items') : t('sidebar.item').toLowerCase()}`}
            date={data.data.invoiceDate}
            active={currentItem === ''}
          />
          <Heading label={`${t('sidebar.item_belong_to')} '${inputValue}'`} />
          {items.map((item) => (
            <OrderViewItem
              key={item}
              id={Number(item)}
            />
          ))}
        </>
      )}
    </div>
  );
};

const OrderViewItem: React.FC<{ id: number }> = ({ id }) => {
  const data = useItem(id);
  const [currentItem, setCurrentItem] = useCurrentOrderItem();

  return (
    <div className={styles.orderItem}>
      {data.kind === 'Called' && (
        <Skeleton
          height={100}
          variant="rounded"
        />
      )}
      {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
      {data.kind === 'Loaded' && (
        <Item
          onClick={() => setCurrentItem(String(data.data.productId))}
          active={String(data.data.productId) === currentItem}
          productId={data.data.productId}
          productName={data.data.productName}
          brandName={data.data.brandName}
          soldDate={data.data.soldDate}
          imageUrl={data.data.imageUrl}
        />
      )}
    </div>
  );
};

export default View;
